import {
  OriginalSchedulesMenu,
  NewSchedulesMenu,
} from 'constant/ScheduleMenuConstant';
import { SuperAdmin } from 'constant/RoleConstant';
import { getAccountUser } from 'store/AccountStore';

const ALWAYS_ALLOWED_EXACT = [
  '/',
  '/profile',
  '/access-denied',
  '/not-found',
  '/request-timed-out',
  '/maintenance',
];

const isAlwaysAllowedPath = (url = '') => {
  return (
    ALWAYS_ALLOWED_EXACT.includes(url) ||
    url.includes('/internal-server') ||
    url.includes('/passwordreset')
  );
};

const resolveUserPageAccess = () => {
  return getAccountUser()?.userPageAccess ?? null;
};

const resolveRoleId = (roleId) => {
  if (roleId !== undefined) {
    return roleId;
  }
  return getAccountUser()?.roleId;
};

const RouterService = {
  isValidUrl(url) {
    const validUrls = [
      '/',
      '/organisation',
      '/email-triggers',
      '/architecture',
      '/break-patterns',
      '/channel-profiles',
      '/library/title-groups',
      '/prioritisation',
      '/title-exclusions',
      '/attributes-override',
      '/schedule',
      '/scheduledetails',
      '/submitted',
      '/export',
      '/users',
      '/channel-groups',
      '/business-rules',
      '/custom-business-rules',
      '/business-rule-definitions',
      '/business-rule-values',
      '/channels',
      '/access-denied',
      '/not-found',
      '/request-timed-out',
      '/maintenance',
      '/profile',
      '/playlist',
      '/view-schedules',
      '/inclusion',
      '/library/programs',
      '/rights',
      '/library/interstitials',
      '/content-types',
      '/library/commercials',
      '/advanced-schedule',
      '/standby-schedule',
      '/presentation-schedule',
      '/notifications',
      '/monitoring'
    ];
    return isAccessible(validUrls, url);
  },

  isAlwaysAllowedPath,

  isPageAccessible(url, options = {}) {
    if (isAlwaysAllowedPath(url)) {
      return true;
    }

    const access =
      options.userPageAccess !== undefined
        ? options.userPageAccess
        : resolveUserPageAccess();

    const allowed = access
      ? (access.allowedRoutes || []).includes(url)
      : isAccessibleByRole(resolveRoleId(options.roleId), url);

    if (url === '/organisation' && resolveRoleId(options.roleId) !== SuperAdmin) {
      const hasMultiple =
        options.hasMultipleOrganisation ??
        getAccountUser()?.hasMultipleOrganisation;
      return allowed && !!hasMultiple;
    }

    return allowed;
  },

  getDefaultLandingPath(options = {}) {
    const access =
      options.userPageAccess !== undefined
        ? options.userPageAccess
        : resolveUserPageAccess();

    const user = getAccountUser();
    const roleId = resolveRoleId(options.roleId);
    const schedulesMenuType =
      options.schedulesMenuType ?? user?.schedulesMenuType;

    const landingOptions = {
      ...options,
      userPageAccess: access,
      roleId,
      schedulesMenuType,
    };

    let candidate;
    if (roleId === 1) {
      candidate = '/organisation';
    } else if (schedulesMenuType === NewSchedulesMenu) {
      candidate = '/advanced-schedule';
    } else {
      candidate = '/schedule';
    }

    if (isLandingPathVisitable(candidate, landingOptions)) {
      return candidate;
    }

    const firstVisitable = (access?.allowedRoutes || []).find((url) =>
      isLandingPathVisitable(url, landingOptions),
    );

    return firstVisitable || candidate;
  },

  isAccessibleForSuperAdmin(url) {
    const accessiblePages = [
      '/',
      '/users',
      '/organisation',
      '/channel-groups',
      '/channels',
      '/business-rules',
      '/custom-business-rules',
      '/business-rule-definitions',
      '/business-rule-values',
      '/email-triggers',
      '/access-denied',
      '/not-found',
      '/request-timed-out',
      '/maintenance',
      '/profile',
    ];
    return isAccessible(accessiblePages, url);
  },

  isAccessibleForOrganisationalAdmin(url) {
    const accessiblePages = [
      '/',
      '/architecture',
      '/break-patterns',
      '/channel-profiles',
      '/library/title-groups',
      '/prioritisation',
      '/title-exclusions',
      '/attributes-override',
      '/schedule',
      '/scheduledetails',
      '/submitted',
      '/export',
      '/users',
      '/channel-groups',
      '/channels',
      '/access-denied',
      '/not-found',
      '/request-timed-out',
      '/maintenance',
      '/profile',
      '/playlist',
      '/view-schedules',
      '/inclusion',
      '/library/programs',
      '/rights',
      '/library/interstitials',
      '/content-types',
      '/organisation',
      '/library/commercials',
      '/advanced-schedule',
      '/standby-schedule',
      '/presentation-schedule',
      '/notifications',
      '/monitoring'
    ];
    return isAccessible(accessiblePages, url);
  },

  isAccessibleForOrganisationalUser(url) {
    const accessiblePages = [
      '/',
      '/architecture',
      '/break-patterns',
      '/channel-profiles',
      '/library/title-groups',
      '/prioritisation',
      '/title-exclusions',
      '/attributes-override',
      '/schedule',
      '/scheduledetails',
      '/submitted',
      '/export',
      '/access-denied',
      '/not-found',
      '/request-timed-out',
      '/maintenance',
      '/profile',
      '/playlist',
      '/view-schedules',
      '/inclusion',
      '/library/programs',
      '/rights',
      '/library/interstitials',
      '/content-types',
      '/organisation',
      '/library/commercials',
      '/advanced-schedule',
      '/standby-schedule',
      '/presentation-schedule',
      '/notifications',
      '/monitoring'
    ];
    return isAccessible(accessiblePages, url);
  },

  isNotAccessibleForUser(url, scheduleViewType) {
    const inaccessiblePagesForOriginalScheduleMenuType = [
      '/export',
      '/advanced-schedule',
      '/standby-schedule',
      '/presentation-schedule',
    ];

    const inaccessiblePagesForNewScheduleMenuType = [
      '/schedule',
      '/view-schedules',
    ];

    if (scheduleViewType === OriginalSchedulesMenu) {
      return isNotAccessible(inaccessiblePagesForOriginalScheduleMenuType, url);
    }

    if (scheduleViewType === NewSchedulesMenu) {
      return isNotAccessible(inaccessiblePagesForNewScheduleMenuType, url);
    }
  },
};

const isAccessible = (accessiblePages, url) => {
  return (
    accessiblePages.includes(url) ||
    url.includes('/internal-server') ||
    url.includes('/passwordreset')
  );
};

const isAccessibleByRole = (roleId, url) => {
  switch (roleId) {
    case 2:
      return RouterService.isAccessibleForOrganisationalAdmin(url);
    case 3:
      return RouterService.isAccessibleForOrganisationalUser(url);
    default:
      return RouterService.isAccessibleForSuperAdmin(url);
  }
};

const isNotAccessible = (inAccessiblePages, url) => {
  return inAccessiblePages.includes(url);
};

const isLandingPathVisitable = (url, options = {}) => {
  return (
    RouterService.isPageAccessible(url, options) &&
    !RouterService.isNotAccessibleForUser(url, options.schedulesMenuType)
  );
};

export default RouterService;
